import { useEffect } from "react";
import { NavLink, Outlet, useLocation, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { KMark } from "@/components/KMark";
import {
  ChartLine, ListDashes, ClipboardText, ShieldWarning, Warning, Moon, CurrencyDollar,
  MapTrifold, MagnifyingGlass, FileText, Users,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/hq",              label: "Control Room",  icon: ChartLine,      end: true },
  { to: "/hq/bookings",     label: "Live Bookings", icon: ListDashes },
  { to: "/hq/verification", label: "Verification",  icon: ClipboardText },
  { to: "/hq/disputes",     label: "Disputes",      icon: Warning },
  { to: "/hq/safety",       label: "Safety",        icon: ShieldWarning },
  { to: "/hq/after-hours",  label: "After-Hours",   icon: Moon },
  { to: "/hq/finance",      label: "Finance",       icon: CurrencyDollar },
  { to: "/hq/marketplace",  label: "Marketplace",   icon: MapTrifold },
  { to: "/hq/lookup",       label: "Lookup",        icon: MagnifyingGlass },
  { to: "/hq/audit",        label: "Audit Log",     icon: FileText },
];

export default function HQLayout() {
  const { user, loading, logout } = useAuth();
  const loc = useLocation();

  if (loading) return <div className="p-8 text-cream/50">Loading…</div>;
  if (!user) return <Navigate to="/auth" replace state={{ from: loc }} />;
  if (!["owner", "hq"].includes(user.role)) {
    return (
      <div className="min-h-screen bg-ink text-cream flex items-center justify-center p-6" data-testid="hq-forbidden">
        <div className="max-w-md border border-red-400/40 p-6 text-center">
          <p className="overline text-red-300">Access denied</p>
          <p className="text-cream mt-2">HQ is an internal operations product. Your role ({user.role}) is not authorised.</p>
          <Link to="/discover" className="btn-gold inline-block mt-6 px-6 py-2 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold">Back to Discover</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink text-cream flex" data-testid="hq-layout">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-cream/10 bg-black/40 flex flex-col">
        <div className="px-5 py-5 border-b border-cream/10 flex items-center gap-3">
          <KMark size={22} />
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-cream/60">KUTZ</p>
            <p className="font-display text-lg leading-none">HQ</p>
          </div>
        </div>
        <div className="px-5 py-3 border-b border-cream/10">
          <span data-testid="hq-demo-badge" className="inline-block px-2 py-1 border border-gold text-gold text-[9px] uppercase tracking-[0.25em] font-semibold">
            HQ DEMO
          </span>
        </div>
        <nav className="flex-1 py-2 overflow-y-auto">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to} to={n.to} end={n.end || false}
                data-testid={`hq-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] border-l-2 transition-colors ${
                    isActive ? "border-gold text-gold bg-gold/5" : "border-transparent text-cream/70 hover:text-cream hover:bg-cream/[0.03]"
                  }`
                }
              >
                <Icon size={14} weight="fill" /> {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="px-5 py-4 border-t border-cream/10">
          <p className="text-[10px] uppercase tracking-[0.2em] text-cream/50">Signed in as</p>
          <p className="text-xs text-cream mt-1">{user.name}</p>
          <p className="text-[10px] text-cream/50">{user.email}</p>
          <button onClick={logout} data-testid="hq-logout" className="mt-3 btn-ghost w-full px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.2em]">Sign out</button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-x-auto">
        <Outlet />
      </main>
    </div>
  );
}
